import type { Variant } from '@scayle/storefront-nuxt'
import { getRestockDate } from '../utils/attribute'
import type { Locale } from '~/config/locales'
import localesConfig, { getLocaleIsoCode } from '~/config/locales'

/**
 * Map an internal shop locale code to its BCP-47 ISO tag for Intl APIs.
 *
 * Shop `locale` values (`de`, `at`, `ch`, `be`, `nl`, ...) are internal
 * identifiers, not language tags. Passing them straight to Intl mis-resolves
 * several shops — most visibly `be` (Belgium) resolves to Belarusian and
 * renders Cyrillic month names, while `at`/`ch` silently fall back to the
 * runtime default (English). We map to the shop's real ISO code (`nl-BE`,
 * `de-AT`, `de-CH`, ...). Unknown values pass through unchanged so callers
 * that already hold a BCP-47 tag keep working. (ECOM-2801)
 */
export const toIsoLocale = (locale: string): string =>
  localesConfig.find((entry) => (entry.code as string) === locale)?.iso ??
  locale

/**
 *
 * @param date
 * @returns Date formatted according to currentShop.locale
 */
export const localeFormattedDate = (date: string): string | null => {
  if (!date || isNaN(new Date(date).valueOf())) {
    return null
  }
  const { locale } = useCurrentShop().value
  return new Intl.DateTimeFormat(toIsoLocale(locale), {
    dateStyle: 'medium',
  }).format(new Date(date))
}

export const getDeliveryEstimation = (
  variant: Variant | undefined,
  deliveryEstimationInDays: number | undefined,
): Date | undefined => {
  if (!deliveryEstimationInDays || !variant) return

  let startDate: Date = new Date()

  if (variant.stock.quantity === 0) {
    const restockDate: Date | undefined = getRestockDate(variant)
    // There may be rare cases where a variant is out-of-stock and no restock date is provided
    if (!restockDate) return

    startDate = restockDate
  }

  return new Date(
    startDate.setDate(startDate.getDate() + deliveryEstimationInDays),
  )
}

export const getDeliveryDaysEstimation = (
  variant: Variant | undefined,
  deliveryEstimationInDays: number | undefined,
) => {
  const deliveryDate = getDeliveryEstimation(variant, deliveryEstimationInDays)

  if (!deliveryDate) {
    return
  }

  return Math.ceil(
    Math.round(deliveryDate.getTime() - new Date().getTime()) /
      (1000 * 3600 * 24),
  )
}

export const getDeliveryEstimationDate = (
  variant: Variant | undefined,
  deliveryEstimationInDays: number | undefined,
): string | undefined => {
  if (!deliveryEstimationInDays || !variant) return

  const { $i18n } = useNuxtApp()
  const currentShop = useCurrentShop()
  const deliveryEstimation: Date | undefined = getDeliveryEstimation(
    variant,
    deliveryEstimationInDays,
  )

  if (deliveryEstimation) {
    const today = new Date()
    const twoWeeksFromNow = new Date(today)
    twoWeeksFromNow.setDate(today.getDate() + 14)

    const isoLocale = getLocaleIsoCode(currentShop.value.locale as Locale)

    const formattedDate = deliveryEstimation.toLocaleDateString(isoLocale, {
      day: 'numeric',
      month: 'numeric',
    })

    // Use weekday only if the date is within the next 2 weeks
    if (deliveryEstimation <= twoWeeksFromNow) {
      const dayOfWeek = deliveryEstimation.toLocaleDateString(isoLocale, {
        weekday: 'long',
      })

      return $i18n.t('date.delivery.estimation_with_weekday', {
        weekday: dayOfWeek,
        date: formattedDate,
      })
    }

    return $i18n.t('date.delivery.estimation', {
      date: formattedDate,
    })
  }

  if (variant.stock.quantity === 0 && !getRestockDate(variant)) {
    return $i18n.t('date.delivery.unknown')
  }

  return undefined
}

/**
 * converts date param to date string in locale format
 * @param date format YYYY-MM-DD
 * @param locale format de-DE (from currentShop)
 */
export const formatLocaleDate = (
  date: string,
  locale: string,
): string | null => {
  if (!date || isNaN(new Date(date).valueOf())) {
    return null
  }
  return new Date(date).toLocaleDateString(toIsoLocale(locale))
}

export default {
  localeFormattedDate,
  getDeliveryEstimationDate,
  formatLocaleDate,
}

/**
 * Format a variant's delivery estimation as a locale-aware date string.
 * Migrated from sources/mad-storefront/utils/date.ts so lifted MAD
 * components in layers/base can resolve their import.
 */
export const getFormattedDeliveryEstimationDate = (
  variant: Variant | undefined,
  deliveryEstimationInDays: number | undefined,
): string | undefined => {
  const currentShop = useCurrentShop()
  const deliveryEstimation = getDeliveryEstimation(
    variant,
    deliveryEstimationInDays,
  )
  return deliveryEstimation?.toLocaleDateString(
    toIsoLocale(currentShop.value.locale),
  )
}
